import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, CircularProgress, Tooltip } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Field, Formik, Form, useFormikContext } from 'formik';
import React from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { SwitchFormField } from 'v2/components/Shared/FormFields/SwitchFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import productService from 'v2/services/product';

import { DropZone } from './dropzone';
import useStyle from './styles';
import PinSchema from './validations';

import type { FormikProps } from 'formik';

export interface IValues {
  instruction: string;
  qt_checkout: boolean;
  qt_warning: boolean;
  buy_limit: boolean;
  qt_limit: number;
  qt_notify: number;
  pins: string[];
}

export default function AddPin({
  open,
  onConfirm,
  onCancel,
  productId,
}: {
  open: boolean;
  onConfirm: (pins: any) => void;
  onCancel: () => void;
  productId: string;
}) {
  const classes = useStyle();

  const [onSubmit] = useCallbackObservable((values: IValues, { resetForm, setSubmitting }: FormikProps<IValues>) => {
    setSubmitting(true);
    return productService.savePins(productId, values.pins).pipe(
      tap(({ data }) => {
        setSubmitting(false);
        toast.success('Pins adicionados com sucesso');
        onConfirm({ ...values, ...{ id: data.key, method_id: 9 } });
        resetForm();
      }),
      catchError((err) => {
        setSubmitting(false);
        toast.error(err);
        return of(true);
      })
    );
  }, []);

  return (
    <Dialog open={open} maxWidth={'sm'} scroll='paper' aria-labelledby='scroll-dialog-title' aria-describedby='scroll-dialog-description'>
      <Formik
        initialValues={{
          instruction: '',
          qt_checkout: false,
          qt_warning: false,
          buy_limit: false,
          qt_limit: 0,
          qt_notify: 0,
          pins: [],
        }}
        onSubmit={onSubmit}
        validationSchema={PinSchema}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <DialogTitle id='customized-dialog-title'>
                <div className={classes.dialogTitle}>Adicionar Pins</div>
              </DialogTitle>
              <DialogContent dividers>
                <PinForm />
              </DialogContent>
              <DialogActions>
                <Button color='primary' disabled={isSubmitting} onClick={onCancel}>
                  Cancelar
                </Button>
                <Button variant='contained' color='secondary' type='submit' disabled={isSubmitting}>
                  Salvar
                  {isSubmitting && <CircularProgress size={15} style={{ color: '#192542', marginLeft: 10 }} />}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

export const PinForm = () => {
  const classes = useStyle();
  const { values } = useFormikContext<IValues>();
  return (
    <Grid container spacing={2} direction='row' justify='flex-start' alignItems='center'>
      <Grid item xs={12}>
        <Field id='qt_checkout' name='qt_checkout' component={SwitchFormField} label='Habilitar quantidade no checkout' />
      </Grid>
      {!!values.qt_checkout && (
        <>
          <Grid item xs={values.buy_limit ? 6 : 12}>
            <Field id='buy_limit' name='buy_limit' component={SwitchFormField} label='Limitar compra' />
          </Grid>
          {!!values.buy_limit && (
            <Grid item xs={6}>
              <Field id='qt_limit' name='qt_limit' component={TextFormField} placeholder='Quantidade de limite de compra' type='number' />
            </Grid>
          )}
        </>
      )}
      <Grid item xs={values.qt_warning ? 6 : 12}>
        <Field id='qt_warning' name='qt_warning' component={SwitchFormField} label='Aviso de estoque acabando' />
      </Grid>
      {!!values.qt_warning && (
        <Grid item xs={6}>
          <Field id='qt_notify' name='qt_notify' component={TextFormField} placeholder='Quantidade miníma' type='number' />
        </Grid>
      )}
      <Grid item xs={12}>
        <Field
          id='instruction'
          name='instruction'
          component={TextFormField}
          placeholder='Adicione um texto de instruções para que o comprador utilize o código adquirido'
          multiline
        />
      </Grid>
      <Grid item xs={12}>
        <Alert severity='info'>
          <p>Cada arquivo deve ter 1 código por linha. (limite de 60 caracteres por linha)</p>
          <p>
            <strong>Formatos:</strong> .csv, .txt
          </p>
          <p>
            <strong>Tamanho máximo:</strong> 8mb por arquivo
          </p>
          <p>
            <Tooltip
              className={classes.linkTooltip}
              classes={{ tooltip: classes.tooltip }}
              title={
                <>
                  <strong>Exemplo de arquivo .txt com pins, um pin em cada linha.</strong>
                  <div className={classes.tooltipTxt}>
                    <p>dff4d5s48</p>
                    <p>59dsf484f</p>
                    <p>81fsd848fsfds</p>
                    <p>fsaf484fsda</p>
                    <p>654g54y46f</p>
                    <p>tre84tre4t</p>
                    <p>t4re84tre4t</p>
                  </div>
                </>
              }
            >
              <a>Veja um exemplo</a>
            </Tooltip>
            <p>
              <strong>Atenção:</strong> Se existir pins iguais em arquivos diferentes, o mesmo só será considerado em um arquivo.
            </p>
          </p>
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <DropZone />
      </Grid>
    </Grid>
  );
};
